import { z } from 'zod'

export { z }

// ── Common reusable schemas ──

export const requiredString = (message = 'この項目は必須です') =>
  z.string().min(1, { message })

export const optionalString = () =>
  z.string().optional().or(z.literal(''))

export const digitsOnly = (message = '数字のみ入力してください') =>
  z.string().regex(/^\d*$/, { message })

export const phoneNumber = (message = '有効な電話番号を入力してください') =>
  z.string().regex(/^[\d\-+()]*$/, { message })

export const municipalityCode = (message = '6桁の数字で入力してください') =>
  z.string().regex(/^\d{6}$/, { message })

export const email = (message = '有効なメールアドレスを入力してください') =>
  z.string().email({ message })

export const minLength = (min: number, message?: string) =>
  z.string().min(min, { message: message ?? `${min}文字以上入力してください` })

export const maxLength = (max: number, message?: string) =>
  z.string().max(max, { message: message ?? `${max}文字以内で入力してください` })

// ── Domain schemas ──

export const loginSchema = z.object({
  loginId: requiredString('ログインIDを入力してください'),
  password: requiredString('パスワードを入力してください'),
})

export const changePasswordSchema = z
  .object({
    currentPassword: requiredString('現在のパスワードを入力してください'),
    newPassword: z
      .string()
      .min(8, { message: 'パスワードは8文字以上入力してください' })
      .regex(/[a-z]/, { message: 'パスワードに英小文字を含めてください' })
      .regex(/[A-Z]/, { message: 'パスワードに英大文字を含めてください' })
      .regex(/\d/, { message: 'パスワードに数字を含めてください' }),
    confirmPassword: requiredString('確認用パスワードを入力してください'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  })

export const userSchema = z.object({
  loginId: requiredString()
    .pipe(minLength(4))
    .pipe(maxLength(50)),
  name: requiredString(),
  role: z.enum([
    'SYSTEM_ADMIN',
    'ADMIN',
    'BUSINESS_CENTER',
    'RECEPTION_COUNTER',
    'CALL_CENTER_SV',
    'CALL_CENTER_AGENT',
    'MUNICIPALITY_STAFF',
  ]),
  municipalityId: z.number().nullable().optional(),
})

export const municipalitySchema = z.object({
  code: municipalityCode(),
  name: requiredString(),
  prefectureName: requiredString(),
})

export const recipientSchema = z.object({
  householdNumber: requiredString(),
  name: requiredString(),
  municipalityId: z.number(),
})

// ── Helper: validate a single value (legacy compat) ──

export type ValidationResult = {
  success: boolean
  error?: string
}

export function validateField<T>(schema: z.ZodType<T>, value: unknown): ValidationResult {
  const result = schema.safeParse(value)
  if (result.success) return { success: true }
  return { success: false, error: result.error.issues[0]?.message }
}

export function validateForm<T>(schema: z.ZodType<T>, data: unknown): {
  success: boolean
  data?: T
  errors?: Record<string, string>
} {
  const result = schema.safeParse(data)
  if (result.success) return { success: true, data: result.data }

  const errors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const key = issue.path.join('.')
    if (!errors[key]) errors[key] = issue.message
  }
  return { success: false, errors }
}
